import { FormProvider } from "react-hook-form";
import classes from "./MedaiLibraryPage.module.less";
import MediaLibrarySearchBox from "./MediaLibrarySearchBox/MediaLibrarySearchBox";
import MediaList from "./MediaList/MediaList";
import Modal from "~/common/components/Modal";
import { Stack } from "@mui/material";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";
import MediaPreview from "./MediaPreview/MediaPreview";
import type { MediaFilterData, MediaFormFields } from "../types/mediaLibraryFormTypes";
import { useMediaLibrary } from "./useMediaLibrary";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";
import { useIsMinTabletSize } from "../../common/hooks/useMediaQuery";
import classnames from "classnames";
import LoginPopup from "~/Login/components/LoginPopup";
import ResetPasswordPopup from "~/Login/components/ResetPasswordPopup";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useCallback } from "react";

interface Props {
  data: MediaFilterData;
}

const MedaiLibraryPage = ({ data }: Props) => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();
  const {
    formProps,
    mediaItems,
    selectedMediaItem,
    isOpen,
    loading,
    error,
    stableFilterOptions,
    errorSnackbarOpen,
    loginPopupOpen,
    handleModalClose,
    handleCardClick,
    fetchMedia,
    setErrorSnackbarOpen,
    handleFavouriteClick,
    updateMediaItems,
    updateSelectedMediaItem,
    handleDownload,
    openLoginPopup,
    closeLoginPopup,
    clearFavFromSessionStorage,
  } = useMediaLibrary(data);
  const { val: resetPopupOpen, setTrue: openResetPopup, setFalse: closeResetPopup } = useBooleanState(false);

  const onFormSubmit = useCallback(
    (formValues: MediaFormFields) => {
      return fetchMedia({ ...formValues, page: 0 }, true);
    },
    [fetchMedia],
  );

  return (
    <FormProvider {...formProps}>
      <div className={classes.container}>
        <h2 className={classes.title}>{t("media.library.pageTitle")}</h2>
        <MediaLibrarySearchBox filterOptions={stableFilterOptions} submitForm={onFormSubmit} />
        {error ? (
          <Alert severity="error" className={classes.error}>
            {error}
          </Alert>
        ) : (
          <MediaList
            mediaItems={mediaItems}
            loading={loading}
            handleCardClick={handleCardClick}
            handleFavouriteClick={handleFavouriteClick}
          />
        )}
      </div>
      {isOpen && selectedMediaItem && (
        <Modal
          title={t("media.library.preview.title")}
          onClose={handleModalClose}
          footerClassName={classnames(!isTablet && classes.footerBtnSection)}
          footerChildren={
            <Stack direction="row" gap={1} className={classnames(!isTablet && "w-100")}>
              <Button variant="text" className={classnames(!isTablet && "w-100")} onClick={handleModalClose}>
                {t("close")}
              </Button>
              <Button
                className={classnames(!isTablet && "w-100")}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDownload();
                }}
              >
                {t("media.library.preview.download")}
              </Button>
            </Stack>
          }
        >
          <MediaPreview
            id={selectedMediaItem.id}
            groupId={selectedMediaItem.groupId}
            updateMediaItems={updateMediaItems}
            updateSelectedMediaItem={updateSelectedMediaItem}
            openLoginPopup={openLoginPopup}
          />
          <Snackbar
            open={errorSnackbarOpen}
            autoHideDuration={3000}
            onClose={() => setErrorSnackbarOpen(false)}
            action={<SnackbarCloseButton onClick={() => setErrorSnackbarOpen(false)} />}
            message={t("media.library.dowloadFailed")}
          />
        </Modal>
      )}
      {loginPopupOpen && (
        <LoginPopup
          initialEmail={""}
          onResetClick={() => {
            openResetPopup();
            closeLoginPopup();
            clearFavFromSessionStorage();
          }}
          close={() => {
            closeLoginPopup();
            clearFavFromSessionStorage();
          }}
        />
      )}
      {resetPopupOpen && <ResetPasswordPopup close={closeResetPopup} initialEmail={""} />}
    </FormProvider>
  );
};

export function createMediaLibraryPage(data: MediaFilterData) {
  return <MedaiLibraryPage data={data} />;
}
